import React from 'react';
import { Typography } from 'antd';
import moment from 'moment';
import { getRefundFeeTypeLabel } from '../../constants/AppConstant';
import {
  BadgeCheck,
  CheckCircle2,
  Clock,
  CreditCard,
  Inbox,
  ShieldCheck,
  Undo2,
  Wallet,
  XCircle,
} from 'lucide-react';

const refundDisplayDate = (value: any, withTime = false) =>
  value ? moment(value).format(withTime ? 'DD-MM-YYYY HH:mm' : 'DD-MM-YYYY') : '—';

const truncateAfterWords = (text: string, wordCount = 5): string => {
  if (!text) return text;
  const words = String(text).trim().split(/\s+/);
  if (words.length <= wordCount) return text;
  return words.slice(0, wordCount).join(' ') + '…';
};

const formatReason = (value: string | null | undefined) =>
  value ? `Reason: ${truncateAfterWords(value, 5)}` : null;

const formatRemarks = (value: string | null | undefined) =>
  value ? `Remarks: ${truncateAfterWords(value, 5)}` : null;

const getRefundHistoryIcon = (step: string) => {
  const normalized = String(step || '').toLowerCase();
  if (normalized.includes('eligib')) return <ShieldCheck size={18} />;
  if (normalized.includes('reconcil')) return <Wallet size={18} />;
  if (normalized.includes('payment')) return <CreditCard size={18} />;
  if (normalized.includes('request')) return <Inbox size={18} />;
  if (normalized.includes('approval') || normalized.includes('approved'))
    return <BadgeCheck size={18} />;
  if (normalized.includes('rejected')) return <XCircle size={18} />;
  if (normalized === 'refunded' || normalized.includes('refunded'))
    return <CheckCircle2 size={18} />;
  if (normalized.includes('refund')) return <CheckCircle2 size={18} />;
  return <Clock size={18} />;
};

const getRefundHistoryIconColors = (step: string) => {
  const normalized = String(step || '').toLowerCase();
  if (normalized.includes('eligib')) return { background: '#f9f0ff', color: '#531dab' };
  if (normalized.includes('reconcil')) return { background: '#e6fffb', color: '#08979c' };
  if (normalized.includes('payment')) return { background: '#e6fffb', color: '#08979c' };
  if (normalized.includes('request')) return { background: '#fff7e6', color: '#d46b08' };
  if (normalized.includes('approval') || normalized.includes('approved'))
    return { background: '#e6f7ff', color: '#096dd9' };
  if (normalized.includes('rejected')) return { background: '#fff1f0', color: '#d4380d' };
  if (normalized === 'refunded' || normalized.includes('refunded'))
    return { background: '#f6ffed', color: '#389e0d' };
  if (normalized.includes('refund')) return { background: '#f6ffed', color: '#389e0d' };
  return { background: '#f5f5f5', color: '#595959' };
};

interface RefundHistoryEntry {
  id: string;
  step: string;
  date: string | undefined;
  performedBy: string;
  description: string;
  amount?: number;
  currency?: string;
  status: 'completed' | 'cancelled' | 'pending';
}

export interface RefundTrackingHistoryProps {
  refund: any;
  currency?: string;
  requestedAmount: number;
}

const RefundTrackingHistory: React.FC<RefundTrackingHistoryProps> = ({
  refund,
  currency = 'USD',
  requestedAmount,
}) => {
  const entries: RefundHistoryEntry[] = [];

  const refundRequestDate = refund?.createdAt || refund?.requestDate;
  entries.push({
    id: 'request',
    step: 'Refund Requested',
    date: refundRequestDate,
    performedBy:
      refund?.name || refund?.applicantName || (refund as any)?.applicantName || 'Customer',
    description: [`Amount: ${currency} ${requestedAmount.toFixed(2)}`].filter(Boolean).join(' | '),
    amount: requestedAmount,
    currency,
    status: 'completed',
  });

  // Internal-only step (Ops Manager / Super Admin); never shown on the user side.
  if (refund?.eligibilityStatus) {
    const isEligible = refund.eligibilityStatus === 'ELIGIBLE';
    const eligibleAmount = Number(refund?.eligibleAmount ?? 0);
    entries.push({
      id: 'eligibility',
      step: isEligible ? 'Eligibility: Eligible' : 'Eligibility: Not Eligible',
      date: refund?.eligibilityAt,
      performedBy: refund?.eligibilityBy || 'Admin',
      description:
        [
          `Fee Type: ${getRefundFeeTypeLabel(refund?.eligibilityFeeType || refund?.refundType)}`,
          isEligible ? `Amount: ${currency} ${eligibleAmount.toFixed(2)}` : null,
          formatRemarks(refund?.eligibilityRemarks),
        ]
          .filter(Boolean)
          .join(' | ') || 'Eligibility confirmed',
      amount: isEligible ? eligibleAmount : undefined,
      currency,
      status: 'completed',
    });
  }

  const status = String(refund?.status || '').toUpperCase();
  const isRejected = status === 'REJECTED' || !!refund?.rejectedAt || !!refund?.rejectedBy;
  const isApproved = status === 'APPROVED' || !!refund?.approvedAt || !!refund?.approvedBy;
  const isRefunded =
    status === 'REFUNDED' ||
    !!refund?.refundDetails?.refundDate ||
    !!refund?.refundDetails?.refundedAt ||
    !!refund?.refundedAt;

  // Accountant payment reconciliation (Application Charges) — internal only, before approval.
  if (refund?.reconciliationAt) {
    const receivedAmount = Number(refund?.reconciliationReceivedAmount ?? 0);
    entries.push({
      id: 'reconciliation',
      step: 'Payment Reconciliation',
      date: refund?.reconciliationReceivedDate || refund?.reconciliationAt,
      performedBy: refund?.reconciliationBy || 'Accountant',
      description: [
        `Received: ${currency} ${receivedAmount.toFixed(2)}`,
        refund?.reconciliationReceivedIn ? `In: ${refund.reconciliationReceivedIn}` : null,
      ]
        .filter(Boolean)
        .join(' | '),
      amount: receivedAmount,
      currency,
      status: 'completed',
    });
  }

  if (isRejected) {
    entries.push({
      id: 'rejected',
      step: 'Rejected',
      date: refund?.rejectedAt,
      performedBy: refund?.rejectedBy || 'Admin',
      description: formatReason(refund?.rejectionReason) || 'Request was rejected',
      status: 'cancelled',
    });
  }

  if (isApproved || isRefunded) {
    const amountApproved = Number(refund?.amountApproved ?? requestedAmount);
    entries.push({
      id: 'approved',
      step: 'Refund Approved',
      date: refund?.approvedAt,
      performedBy: refund?.approvedBy || 'Admin',
      description: [`Amount: ${currency} ${amountApproved.toFixed(2)}`].filter(Boolean).join(' | '),
      amount: amountApproved,
      currency,
      status: 'completed',
    });
  }

  if (isRefunded) {
    const refundDetails = refund?.refundDetails;
    const refundAmount = Number(
      refundDetails?.refundAmount ?? refund?.refundAmount ?? requestedAmount
    );
    entries.push({
      id: 'refunded',
      step: 'Refunded',
      date:
        refundDetails?.refundDate ||
        refundDetails?.refundedAt ||
        refund?.refundedAt ||
        (refund as any)?.refundedAt,
      performedBy: refundDetails?.refundedBy || 'Admin',
      description: [refundAmount > 0 ? `Amount: ${currency} ${refundAmount.toFixed(2)}` : null]
        .filter(Boolean)
        .join(' | '),
      amount: refundAmount,
      currency,
      status: 'completed',
    });
  }

  const sortedEntries = entries;

  return (
    <div
      style={{
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        padding: '24px 28px',
      }}
    >
      <div
        style={{
          marginBottom: '16px',
          paddingBottom: '12px',
          borderBottom: '1px solid #f1f5f9',
        }}
      >
        <Typography.Title
          level={5}
          style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: '#004195',
          }}
        >
          Refund History
        </Typography.Title>
      </div>

      {sortedEntries.length === 0 ? (
        <div style={{ color: '#64748b', fontSize: '14px' }}>No tracking history yet.</div>
      ) : (
        <div style={{ position: 'relative', paddingLeft: '42px' }}>
          {sortedEntries.map((entry, index) => {
            const isLast = index === sortedEntries.length - 1;
            const isCancelled = entry.status === 'cancelled';
            const iconColors = getRefundHistoryIconColors(entry.step);

            return (
              <div
                key={entry.id}
                style={{
                  position: 'relative',
                  paddingBottom: isLast ? 0 : '24px',
                }}
              >
                {!isLast && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '-29px',
                      top: '30px',
                      bottom: 0,
                      width: '2px',
                      background: '#075dc5',
                    }}
                  />
                )}
                <div
                  style={{
                    position: 'absolute',
                    left: '-42px',
                    top: 0,
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: iconColors.background,
                    color: iconColors.color,
                    border: `2px solid ${iconColors.color}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                  }}
                >
                  {getRefundHistoryIcon(entry.step)}
                </div>
                <div
                  style={{
                    borderRadius: '8px',
                    border: `1px solid ${isCancelled ? '#ffccc7' : '#e6edf5'}`,
                    background: isCancelled ? '#fffafa' : '#fafcff',
                    padding: '14px 18px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '8px',
                      marginBottom: '6px',
                    }}
                  >
                    <Typography.Title
                      level={5}
                      style={{
                        margin: 0,
                        fontSize: '15px',
                        color: isCancelled ? '#cf1322' : '#1a1a1a',
                      }}
                    >
                      {entry.step} <span style={{}}>| </span>
                      <span style={{ color: '#595959', fontSize: '14px', lineHeight: 1.55 }}>
                        {entry.description}
                      </span>
                    </Typography.Title>
                    <span
                      style={{
                        padding: '2px 10px',
                        borderRadius: '12px',
                        background: isCancelled ? '#fff1f0' : '#e6f4ea',
                        color: isCancelled ? '#cf1322' : '#2e7d32',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      {isCancelled ? 'Cancelled' : 'Completed'}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#075dc5',
                      fontSize: '13px',
                      fontWeight: 600,
                    }}
                  >
                    <span>{refundDisplayDate(entry.date, true)}</span>
                    <span style={{ color: '#bfbfbf' }}>·</span>
                    <span style={{ color: '#60758d', fontWeight: 500 }}>{entry.performedBy}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RefundTrackingHistory;
